import useTranslation from "@babackapp/translations/use-translation";
import i18n from "i18next";
import { useMemo } from "react";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team";
import { useShopFlag } from "@/hooks/use-flag";
import ColissimoInfoFrImage from "@/assets/infoFr.png";
import ColissimoInfoEnImage from "@/assets/infoEn.png";
import ColissimoInfoEsImage from "@/assets/infoEs.png";
import { useCarrierCatalogQuery } from "@babackapp/private-api-client/client/use-carrier-catalog-query";
import {
  CarrierCatalogEntryDto,
  CarrierCredentialFieldDto,
  UpdateChronopostPreferencesDto,
  UpdateColissimoCarrierPreferences,
  UpdateDpdCarrierPreferencesDto,
  UpdateDhlCarrierPreferencesDto,
  UpdateMondialRelayCarrierPreferencesDto,
} from "@babackapp/private-api-client/types";
import { useCarriersContext } from "../use-carriers-context";

export type CarrierCodePickableType = CarrierCatalogEntryDto["code"];

export const UPS_CARRIER_CODES: readonly string[] = [
  "ups-saver",
  "ups-standard",
  "ups-ground",
];

export type CredentialsType =
  | UpdateChronopostPreferencesDto
  | UpdateColissimoCarrierPreferences
  | UpdateDpdCarrierPreferencesDto
  | UpdateDhlCarrierPreferencesDto
  | UpdateMondialRelayCarrierPreferencesDto;

export type FieldInfo = {
  placeholder: string | boolean;
  label: string;
  type: CarrierCredentialFieldDto["type"];
  required: boolean;
  options?: { value: string; label: string }[];
  /**
   * Short note rendered under the input. Use it when the label alone cannot
   * say where the value comes from — DHL Parcel DE asks for two unrelated
   * pairs of credentials, and a merchant who put their account login in the
   * API key field spent a week on a 401 (BBS-330).
   */
  hint?: string;
};

export type ConnectionInfo = {
  [fieldKey: string]: FieldInfo;
};

type CarrierCredentialFields = {
  value: CarrierCodePickableType;
  label: string;
  connectionInfo: ConnectionInfo;
};

type FieldPresentation = Partial<
  Pick<FieldInfo, "label" | "placeholder" | "hint">
>;

/**
 * The picker itself comes from the API (`GET /shops/{shopId}/carriers/catalog`):
 * which carriers, which fields, which are required. It used to be a list kept
 * here, and drifted from what the API accepts until Chronopost, bpost and
 * Bleckmann were missing from it (BBS-429).
 *
 * What stays here is presentation only: translated labels, placeholders and
 * hints, by carrier then field. A field without an entry shows the API label,
 * which is how the staff-only carriers are displayed.
 */
export const useCarriersVariables = () => {
  const t = useTranslation();
  const { shop } = useCarriersContext();
  const { data: catalog, isLoading } = useCarrierCatalogQuery(shop.id);
  const isSuperAdmin = useViewerIsSuperAdmin();
  const hasFedexFlag = useShopFlag("fedex-carrier");
  const infoImageUrlByLocale: {
    [key: string]: string;
  } = {
    fr: ColissimoInfoFrImage,
    "fr-FR": ColissimoInfoFrImage,
    en: ColissimoInfoEnImage,
    "en-US": ColissimoInfoEnImage,
    es: ColissimoInfoEsImage,
    "es-ES": ColissimoInfoEsImage,
    de: ColissimoInfoEnImage,
    "de-DE": ColissimoInfoEnImage,
  };

  // Memoised: the transport form derives its schema from these objects and
  // revalidates when it changes, so a fresh array on every render loops.
  // Keyed on the language because `t` itself is recreated on every render.
  const listCarriers = useMemo((): CarrierCredentialFields[] => {
    const passwordField: FieldPresentation = {
      placeholder: "*****************",
      label: t("carrier.securityKey"),
    };

    const idPlaceholder = t("carrier.idPlaceholder");
    const idLabel = t("carrier.idLabel");
    const shipperAccountNumberLabel = t("carrier.shipperAccountNumber");
    const shipperAccountNumber: FieldPresentation = {
      placeholder: idPlaceholder,
      label: shipperAccountNumberLabel,
    };
    const dhlExpressFields: Record<string, FieldPresentation> = {
      webSiteId: { placeholder: idPlaceholder, label: t("carrier.websiteId") },
      securityKey: passwordField,
      shipperAccountNumber,
    };
    const upsFields: Record<string, FieldPresentation> = {
      accountNumber: shipperAccountNumber,
    };

    const presentation: Partial<
      Record<CarrierCodePickableType, Record<string, FieldPresentation>>
    > = {
      colissimo: {
        accountId: { placeholder: idPlaceholder, label: idLabel },
        password: passwordField,
        SLS: { placeholder: true, label: t("carrier.SLSAccount") },
        // Colissimo asks for it on the customs declaration of a return from
        // outside the EU (BBS-431). Same column track-office fills.
        eori: {
          placeholder: "FR12345678900000",
          label: t("carrier.eori"),
          hint: t("carrier.eoriHint"),
        },
      },
      "mondial-relay": {
        brand: {
          placeholder: t("carrier.brandPlaceholder"),
          label: t("carrier.brandLabel"),
        },
        login: {
          placeholder: idPlaceholder,
          label: t("carrier.login") + " (API V2)",
        },
        password: {
          placeholder: "*****************",
          label: t("settings.carriers.password") + " (API V2)",
        },
        securityKey: {
          placeholder: "*****************",
          label: t("carrier.securityKey") + " (API V1)",
        },
      },
      "dhl-express": dhlExpressFields,
      "dhl-economy-select": dhlExpressFields,
      "dhl-parcel-de": {
        // Two unrelated credential pairs: the API key/secret come from the
        // developer portal, the username/password from the business portal.
        // Keys are DHL-specific rather than the shared carrier.appClientId,
        // which FedEx also uses.
        clientId: {
          placeholder: "a1b2c3d4-...",
          label: t("carrier.dhlParcelDe.apiKey"),
          hint: t("carrier.dhlParcelDe.apiKeyHint"),
        },
        clientSecret: {
          placeholder: "*****************",
          label: t("carrier.dhlParcelDe.apiSecret"),
        },
        username: {
          placeholder: idPlaceholder,
          label: t("carrier.dhlParcelDe.portalUsername"),
          hint: t("carrier.dhlParcelDe.portalHint"),
        },
        password: {
          placeholder: "*****************",
          label: t("carrier.dhlParcelDe.portalPassword"),
        },
        receiverId: {
          placeholder: "deu",
          label: t("carrier.dhlParcelDe.receiverId"),
          hint: t("carrier.dhlParcelDe.receiverIdHint"),
        },
      },
      "ups-standard": upsFields,
      "ups-saver": upsFields,
      "ups-ground": upsFields,
      chronopost: {
        accountNumber: shipperAccountNumber,
        password: passwordField,
        subAccount: { placeholder: "", label: t("carrier.subAccount") },
        // Without it the API used to fall back to Shop2Shop in silence, which
        // is why Chronopost stayed out of the picker until BBS-429.
        chronopostService: {
          label: t("carrier.chronopost.service"),
          hint: t("carrier.chronopost.serviceHint"),
        },
        chronopostProductCode: {
          placeholder: "01",
          label: t("carrier.chronopost.productCode"),
          hint: t("carrier.chronopost.productCodeHint"),
        },
      },
      dpd: {
        userid: { placeholder: idPlaceholder, label: idLabel },
        password: passwordField,
        customerCountryCode: {
          placeholder: t("carrier.customerCountryCodePlaceholder"),
          label: t("carrier.customerCountryCode"),
        },
        customerCenterNumber: {
          placeholder: t("carrier.customerCenterNumberPlaceholder"),
          label: t("carrier.customerCenterNumber"),
        },
        customerNumber: {
          placeholder: t("carrier.customerNumberPlaceholder"),
          label: t("carrier.customerNumber"),
        },
      },
      "dpd-ch": {
        username: { placeholder: idPlaceholder, label: idLabel },
        password: passwordField,
        invoicingNumber: {
          placeholder: t("carrier.customerNumberPlaceholder"),
          label: "Invoicing Number",
        },
      },
      "swiss-post": {
        // The client comes from developer.apis.post.ch, the franking licence
        // from the Swiss Post contract: say so, the DHL Parcel DE form did not
        // and a merchant mixed up the two sources for a week (BBS-330).
        clientId: {
          placeholder: "a1b2c3d4-...",
          label: t("carrier.swissPost.clientId"),
          hint: t("carrier.swissPost.clientIdHint"),
        },
        clientSecret: {
          placeholder: "*****************",
          label: t("carrier.swissPost.clientSecret"),
          // Same client, optional second subscription: without it, return
          // statuses fall back to TrackingMore, which is billed.
          hint: t("carrier.swissPost.trackingHint"),
        },
        frankingLicense: {
          placeholder: "60001234",
          label: t("carrier.swissPost.frankingLicense"),
          hint: t("carrier.swissPost.frankingLicenseHint"),
        },
        service: { label: t("carrier.swissPost.service") },
        domicilePostOffice: {
          placeholder: "3030 Bern",
          label: t("carrier.swissPost.domicilePostOffice"),
          hint: t("carrier.swissPost.domicilePostOfficeHint"),
        },
        isDev: {
          label: t("carrier.swissPost.testMode"),
          hint: t("carrier.swissPost.testModeHint"),
        },
      },
      bepost: {
        accountId: { placeholder: idPlaceholder, label: idLabel },
        password: {
          placeholder: "*****************",
          label: t("carrier.bpost.passphrase"),
        },
        trackingPassword: {
          placeholder: "*****************",
          label: t("carrier.bpost.trackingPassphrase"),
        },
      },
      bleckmann: {
        accessToken: {
          placeholder: "*****************",
          label: t("carrier.bleckmann.apiKey"),
        },
        userCustomerID: {
          placeholder: idPlaceholder,
          label: t("carrier.bleckmann.customerId"),
        },
        customerCountryCode: { label: t("carrier.bleckmann.warehouseId") },
        isDev: { label: t("carrier.bleckmann.testMode") },
      },
      fedex: {
        clientId: {
          placeholder: t("carrier.appClientId"),
          label: t("carrier.appClientId"),
        },
        clientSecret: {
          placeholder: t("carrier.appClientSecret"),
          label: t("carrier.appClientSecret"),
        },
        accountNumber: shipperAccountNumber,
        shipperCountryCode: {
          placeholder: "Shipper Country Code (e.g., FR, US) (optional)",
          label: "Shipper Country Code",
        },
        isDev: { label: "Dev Mode (use FedEx sandbox)" },
      },
    };

    const carrierLabel = (entry: CarrierCatalogEntryDto) => {
      if (entry.code === "no-physical-return") {
        return t("carrier.noPhysicalReturn");
      }
      return entry.visibility === "super-admin"
        ? `${entry.label} · Super admin`
        : entry.label;
    };

    const toConnectionInfo = (entry: CarrierCatalogEntryDto): ConnectionInfo =>
      Object.fromEntries(
        entry.fields.map((field) => {
          const shown = presentation[entry.code]?.[field.key];
          return [
            field.key,
            {
              type: field.type,
              required: field.required,
              options: field.options ?? undefined,
              label: shown?.label ?? field.label,
              placeholder:
                shown?.placeholder ??
                (field.type === "password" ? "*****************" : ""),
              hint: shown?.hint,
            },
          ];
        }),
      );

    return (
      (catalog ?? [])
        // Only one shop flag exists today. An unknown one keeps the carrier away
        // from merchants rather than showing it ungated.
        .filter(
          (entry) =>
            !entry.featureFlag ||
            isSuperAdmin ||
            (entry.featureFlag === "fedex-carrier" && hasFedexFlag),
        )
        .map((entry) => ({
          value: entry.code,
          label: carrierLabel(entry),
          connectionInfo: toConnectionInfo(entry),
        }))
    );
  }, [catalog, isSuperAdmin, hasFedexFlag, i18n.language]);

  return {
    listCarriers,
    isLoading,
    infoImageUrlByLocale,
  };
};
